import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { PrivateService } from 'app/core/services/private.service';
import { ToastrService } from 'ngx-toastr';
import { map, Observable, startWith } from 'rxjs';

interface ImportResponse {
  message: string;
}

@Component({
  selector: 'dialog-import-dat',
  templateUrl: './dialog-import-dat.component.html',
  styleUrls: ['./dialog-import-dat.component.scss']
})
export class DialogImportDatComponent implements OnInit {
  isLoading: boolean = false;
  selectedFile: File | null = null;
  selectedSource: 'machine' | 'web' | 'zkmachine' | null = null;

  selectBranch: any = '';
  branchCtrl = new FormControl('');
  filteredBranch!: Observable<any[]>;
  @ViewChild('triggerBranch') autoBranch!: MatAutocompleteTrigger;
  branches: any[] = [];
  branch: any;

  dateFrom: Date | null = null;
  dateTo: Date | null = null;

  uploaded = {
    machine: false,
    web: false,
    zkmachine: false,
  };

  importResult: ImportResponse | null = null;
  importError: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<DialogImportDatComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private privateService: PrivateService,
    private toastr: ToastrService
  ) { }

  ngOnInit(): void {
    this.getBranches();
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  getBranches() {
    this.privateService.getBranchesAll().subscribe(response => {
      this.branches = response;
      this.filteredBranch = this.branchCtrl.valueChanges.pipe(
        startWith(''),
        map(value => this._filter(value, this.branches, 'Name'))
      );
    });
  }

  onSelect(name: string): void {
    const selected: any = this.branches.find(d => d.Name === name);
    this.branch = selected.C_Activity_ID;
  }

  private formatDate(date: Date | null): string | null {
    if (!date) return null;
    const d = new Date(date);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd} 00:00:00.0`;
  }

  private _filter(value: string, array: any[], key: string): any[] {
    const filterValue = value?.toLowerCase() || '';
    return array?.filter(item => item[key]?.toLowerCase().includes(filterValue)) || [];
  }

  onFileSelected(event: Event, source: 'machine' | 'web' | 'zkmachine'): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    this.selectedFile = file;
    this.selectedSource = source;
  }

  uploadDatFile(): void {
    if (!this.selectedFile || !this.selectedSource) return;

    if (!this.branch) {
      this.toastr.warning('Please select a branch before uploading.', 'Branch Required', {
        timeOut: 3000, closeButton: true, positionClass: 'toast-top-center'
      });
      return;
    }


    this.isLoading = true;

    const reader = new FileReader();
    reader.onload = () => {
      const fileContent = reader.result as string;
      const payload = {
        fileName: this.selectedFile!.name,
        fileContent,
        C_Activity_ID: this.branch,
        dateFrom: this.formatDate(this.dateFrom),
        dateTo: this.formatDate(this.dateTo),
      };

      console.log('Uploading with payload:', payload);
      
      if (this.selectedSource === 'machine') {
        this.privateService.importDatMachine(payload).subscribe({
          next: (res: ImportResponse) => {
            this.handleImportResponse(res, 'machine');
          },
          error: (err) => {
            console.error('Machine upload error', err);
            this.handleImportError(err, 'machine');
          },
        });
      } else if (this.selectedSource === 'web') {
        this.privateService.importDatWeb(payload).subscribe({
          next: (res: ImportResponse) => {
            this.handleImportResponse(res, 'web');
          },
          error: (err) => {
            console.error('Web upload error', err);
            this.handleImportError(err, 'web');
          },
        });
      } else if (this.selectedSource === 'zkmachine') {
        this.privateService.importZKMachine(payload).subscribe({
          next: (res: ImportResponse) => {
            this.handleImportResponse(res, 'zkmachine');
          },
          error: (err) => {
            console.error('ZK Machine upload error', err);
            this.handleImportError(err, 'zkmachine');
          },
        });
      }
    };

    reader.onerror = () => {
      this.isLoading = false;
      this.toastr.error('Failed to read file', 'File Read Error', {
        timeOut: 5000,
        closeButton: true,
        positionClass: 'toast-top-center'
      });
    };

    reader.readAsText(this.selectedFile);
  }

  private handleImportResponse(res: ImportResponse, source: 'machine' | 'web' | 'zkmachine'): void {
    this.importResult = { message: res?.message || 'Request completed.' };
    this.uploaded[source] = true;
    this.resetUploadState(false);
  }

  private handleImportError(err: any, source: 'machine' | 'web' | 'zkmachine'): void {
    this.importError = err?.error?.message || err?.message || 'An unexpected error occurred during import.';
    this.isLoading = false;
  }

  private resetUploadState(clearSuccess: boolean = true): void {
    this.selectedFile = null;
    this.selectedSource = null;
    this.isLoading = false;
    if (clearSuccess) {
      this.importResult = null;
      this.importError = null;
    }
  }

}